import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { AppHeader } from '@/components/AppHeader';
import { 
  Trophy,
  CalendarIcon,
  Clock,
  Users,
  Lock,
  Globe,
  CheckCircle,
  Lightbulb,
  ArrowLeft
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface CreateLeagueFormData {
  name: string;
  description: string;
  leagueType: 'public' | 'private';
  password: string;
  maxParticipants: number;
  draftDate: Date | null;
  draftTime: string;
  scoringType: string;
  rosterSize: string;
}

const CreateLeague = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<CreateLeagueFormData>({
    name: '',
    description: '',
    leagueType: 'public',
    password: '',
    maxParticipants: 8,
    draftDate: null,
    draftTime: '19:00',
    scoringType: '',
    rosterSize: '11 starters + 10 bench'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdLeagueId, setCreatedLeagueId] = useState<string>('');

  const handleInputChange = (field: keyof CreateLeagueFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to create a league.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: 'League name required',
        description: 'Please enter a name for your league.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.draftDate) {
      toast({
        title: 'Draft date required',
        description: 'Please select a draft date.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.scoringType) {
      toast({
        title: 'Scoring type required',
        description: 'Please select a scoring type.',
        variant: 'destructive',
      });
      return;
    }

    if (formData.leagueType === 'private' && !formData.password.trim()) {
      toast({
        title: 'Password required',
        description: 'Please enter a password for your private league.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Combine date and time
      const draftDateTime = new Date(formData.draftDate);
      const [hours, minutes] = formData.draftTime.split(':').map(Number);
      draftDateTime.setHours(hours, minutes, 0, 0);

      // Create end date (1 week after draft for example)
      const endDate = new Date(draftDateTime);
      endDate.setDate(endDate.getDate() + 7);

      const { data: league, error } = await supabase
        .from('leagues')
        .insert({
          name: formData.name.trim(),
          owner_id: user.id,
          is_private: formData.leagueType === 'private',
          password_hash: formData.leagueType === 'private' ? formData.password : null,
          draft_at: draftDateTime.toISOString(),
          starters_count: 11,
          bench_count: 10
        })
        .select()
        .single();

      if (error) throw error;

      // Auto-join the creator to the league
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, username')
        .eq('user_id', user.id)
        .maybeSingle()
      
      await supabase
        .from('league_players')
        .insert({
          league_id: league.id,
          user_id: user.id
        });

      setCreatedLeagueId(league.id);
      setIsSuccess(true);

      toast({
        title: 'League created successfully!',
        description: 'Your league has been created and you\'ve been added as the first participant.',
      });

    } catch (error: any) {
      console.error('Error creating league:', error);
      toast({
        title: 'Failed to create league',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/leagues');
  };

  const handleGoToLeague = () => {
    navigate(`/league/${createdLeagueId}`);
  };

  if (isSuccess) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <AppHeader />
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-8">
              <div className="max-w-md mx-auto">
                <Card className="text-center">
                  <CardContent className="pt-8 pb-8">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold mb-2">League Created Successfully!</h2>
                    <p className="text-muted-foreground mb-6">
                      Your league "{formData.name}" has been created and is ready for participants to join.
                    </p>
                    <div className="space-y-3">
                      <Button 
                        onClick={handleGoToLeague}
                        className="w-full bg-primary hover:bg-primary/90"
                      >
                        <Trophy className="h-4 w-4 mr-2" />
                        Go to League
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate('/leagues')}
                        className="w-full"
                      >
                        Back to League Lobby
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto px-4 py-6">
            <div className="flex gap-6">
              {/* Main Content */}
              <div className="flex-1 max-w-2xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                  <Button
                    variant="ghost"
                    onClick={handleCancel}
                    className="mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to League Lobby
                  </Button>
                  <h1 className="text-3xl font-bold mb-2">Create New League</h1>
                  <p className="text-muted-foreground">
                    Set up your league and invite players to join your competition
                  </p>
                </div>

                {/* Form Card */}
                <Card className="rounded-xl shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-primary" />
                      League Details
                    </CardTitle>
                    <CardDescription>
                      Configure your league settings and preferences
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* League Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium">
                          League Name *
                        </Label>
                        <Input
                          id="name"
                          placeholder="Enter league name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                      </div>

                      {/* Description */}
                      <div className="space-y-2">
                        <Label htmlFor="description" className="text-sm font-medium">
                          Description (Optional)
                        </Label>
                        <Textarea
                          id="description"
                          placeholder="Describe your league (rules, prizes, etc.)"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="min-h-[80px] focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                      </div>

                      {/* League Type */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">League Type *</Label>
                        <RadioGroup
                          value={formData.leagueType}
                          onValueChange={(value: 'public' | 'private') => handleInputChange('leagueType', value)}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="public" id="public" />
                            <Label htmlFor="public" className="flex items-center gap-2 cursor-pointer">
                              <Globe className="h-4 w-4" />
                              Public
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="private" id="private" />
                            <Label htmlFor="private" className="flex items-center gap-2 cursor-pointer">
                              <Lock className="h-4 w-4" />
                              Private
                            </Label>
                          </div>
                        </RadioGroup>

                        {/* Password Field (conditional) */}
                        {formData.leagueType === 'private' && (
                          <div className="space-y-2 animate-fade-in">
                            <Label htmlFor="password" className="text-sm font-medium">
                              League Password *
                            </Label>
                            <Input
                              id="password"
                              type="password"
                              placeholder="Enter league password"
                              value={formData.password}
                              onChange={(e) => handleInputChange('password', e.target.value)}
                              className="focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                          </div>
                        )}
                      </div>

                        {/* Number of Players */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Number of Players *</Label>
                          <Select
                            value={formData.maxParticipants.toString()}
                            onValueChange={(value) => handleInputChange('maxParticipants', parseInt(value))}
                          >
                            <SelectTrigger className="focus:ring-2 focus:ring-primary focus:border-primary">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="4">4 Players</SelectItem>
                              <SelectItem value="6">6 Players</SelectItem>
                              <SelectItem value="8">8 Players</SelectItem>
                              <SelectItem value="10">10 Players</SelectItem>
                              <SelectItem value="12">12 Players</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                      {/* Draft Date & Time */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Draft Date *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal focus:ring-2 focus:ring-primary focus:border-primary",
                                  !formData.draftDate && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.draftDate ? format(formData.draftDate, "PPP") : "Select date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={formData.draftDate || undefined}
                                onSelect={(date) => handleInputChange('draftDate', date)}
                                disabled={(date) => date < new Date()}
                                initialFocus
                                className="p-3 pointer-events-auto"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="draftTime" className="text-sm font-medium">Draft Time *</Label>
                          <div className="relative">
                            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="draftTime"
                              type="time"
                              value={formData.draftTime}
                              onChange={(e) => handleInputChange('draftTime', e.target.value)}
                              className="pl-10 focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Scoring Type */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Scoring Type *</Label>
                        <Select
                          value={formData.scoringType}
                          onValueChange={(value) => handleInputChange('scoringType', value)}
                        >
                          <SelectTrigger className="focus:ring-2 focus:ring-primary focus:border-primary">
                            <SelectValue placeholder="Select scoring system" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="classic">Classic Points</SelectItem>
                            <SelectItem value="head-to-head">Head-to-Head</SelectItem>
                            <SelectItem value="both">Both</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Roster Size */}
                      <div className="space-y-2">
                        <Label htmlFor="rosterSize" className="text-sm font-medium">
                          Roster Size
                        </Label>
                        <Input
                          id="rosterSize"
                          placeholder="e.g., 11 starters + 10 bench"
                          value={formData.rosterSize}
                          onChange={(e) => handleInputChange('rosterSize', e.target.value)}
                          className="focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                      </div>

                      {/* Buttons */}
                      <div className="space-y-3 pt-4">
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-success hover:bg-success/90 text-white font-semibold h-12"
                        >
                          {isSubmitting ? (
                            <>Creating League...</>
                          ) : (
                            <>
                              <Trophy className="h-4 w-4 mr-2" />
                              Create League
                            </>
                          )}
                        </Button>
                        
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancel}
                          className="w-full"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar */}
              <div className="w-80 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-primary" />
                      Tips for Creating a League
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <span>Choose a memorable name that reflects your group or theme</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <span>Set a draft date when all players can join and participate</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <span>Use a password for private groups to control who can join</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <span>Consider the time zone of your participants when scheduling</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <span>Start with 8-10 teams for the best balance of competition</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Next Steps
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                        <span>Create your league</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-muted text-muted-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                        <span>Share invite link with friends</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-muted text-muted-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                        <span>Wait for players to join</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-muted text-muted-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">4</span>
                        <span>Start your draft on schedule</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default CreateLeague;